import * as RS from '@radix-ui/react-select'
import { clsx } from 'clsx'
import { Check, ChevronDown } from 'lucide-react'
import type { ReactNode } from 'react'

export interface SelectOption<T extends string> {
  value: T
  label: string
  group?: string
}

interface SelectProps<T extends string> {
  value: T | undefined
  onChange: (value: T) => void
  options: SelectOption<T>[]
  placeholder?: string
  icon?: ReactNode
  label?: string
  className?: string
  disabled?: boolean
}

export function Select<T extends string>({ value, onChange, options, placeholder, icon, label, className, disabled }: SelectProps<T>) {
  return (
    // '' shows the placeholder and keeps the select controlled while nothing is chosen yet
    <RS.Root value={value ?? ''} onValueChange={(v) => onChange(v as T)} disabled={disabled}>
      <RS.Trigger
        aria-label={label}
        className={clsx(
          'input flex items-center gap-2 text-left data-[disabled]:cursor-not-allowed data-[disabled]:opacity-60 data-[placeholder]:text-faint',
          className,
        )}
      >
        {icon && <span className="text-muted [&>svg]:size-4">{icon}</span>}
        <span className="min-w-0 flex-1 truncate">
          <RS.Value placeholder={placeholder} />
        </span>
        <RS.Icon>
          <ChevronDown className="size-4 text-muted" />
        </RS.Icon>
      </RS.Trigger>
      <RS.Portal>
        <RS.Content
          position="popper"
          sideOffset={6}
          collisionPadding={12}
          className="z-50 max-h-[min(320px,var(--radix-select-content-available-height))] w-[var(--radix-select-trigger-width)] min-w-56 overflow-hidden rounded-xl border border-border bg-surface-2 shadow-xl shadow-black/40"
        >
          <RS.Viewport className="p-1">
            {[...new Set(options.map((o) => o.group ?? ''))].map((group) => {
              const items = options.filter((o) => (o.group ?? '') === group)
              return <RS.Group key={group}>
                {group && <RS.Label className="px-2 py-2 text-xs text-muted">{group}</RS.Label>}
{items.map((o) => (
              <RS.Item
                key={o.value}
                value={o.value}
                className="relative flex cursor-pointer items-center rounded-lg py-2 pr-2.5 pl-8 text-sm outline-none select-none data-[highlighted]:bg-surface-3"
              >
                <RS.ItemIndicator className="absolute left-2.5">
                  <Check className="size-4 text-accent" />
                </RS.ItemIndicator>
                <RS.ItemText>{o.label}</RS.ItemText>
              </RS.Item>
))}
              </RS.Group>
            })}
          </RS.Viewport>
        </RS.Content>
      </RS.Portal>
    </RS.Root>
  )
}
